import React, { useRef } from 'react';
import { Breakpoint, grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import {
  IconFilter,
  Media,
  NumericalLozenge,
  TextButton,
} from '@noths/polaris-client-ribbons-design-system';

import * as elementIds from 'src/components/organisms/FilterMenu/constants/elementIds';
import { SortByContainer } from 'src/components/organisms/FilterMenu/containers/SortByContainer';
import { useStickyNav } from 'src/components/organisms/FilterMenu/hooks/useSlimStickyNav';
import * as styles from 'src/components/organisms/FilterMenu/styles/SlimFilterMenu.styles';
import type { SlimFilterMenuProps } from 'src/components/organisms/FilterMenu/types';
import { getSlimFilterMenuLabels } from './getSlimFilterMenuLabels';

export const SlimFilterMenu = ({
  onVisible,
  openFilterModal,
  onResetFilters,
  pageProductsCount,
  totalFiltersAppliedCount,
  totalSelectedFilterOptionsCount,
  totalProducts,
  selectedSortOption,
}: SlimFilterMenuProps) => {
  const navRef = useRef<HTMLDivElement>(null);
  const stickyNav = useStickyNav(navRef.current, {
    productsCount: pageProductsCount,
    onFirstStickyVisibility: onVisible,
  });

  const {
    accessibleFilterLabel,
    filterLabel,
    filterAndSortLabel,
    resetAllLabel,
    accessibleResetAllLabel,
  } = getSlimFilterMenuLabels(totalSelectedFilterOptionsCount, selectedSortOption);

  return (
    <>
      <Media lessThan={Breakpoint.L} css={styles.smallScreenMenuWrapper}>
        <div
          css={styles.smallScreenMenu}
          data-testid={elementIds.STICKY_NAV}
          data-is-sticky={stickyNav.isSticky}
          data-is-visible={stickyNav.isVisible}
          ref={navRef}
        >
          <div css={styles.smallScreenCTAWrapper}>
            <p css={styles.totalProducts}>{totalProducts} products</p>
            <button
              type="button"
              data-testid={elementIds.BUTTON_OPEN_MODAL}
              onClick={() =>
                openFilterModal({ totalFiltersAppliedCount, totalSelectedFilterOptionsCount })
              }
              css={styles.filterButton}
              aria-label={accessibleFilterLabel}
            >
              {totalSelectedFilterOptionsCount > 0 && (
                <NumericalLozenge
                  value={
                    totalSelectedFilterOptionsCount > 99
                      ? '99+'
                      : `${totalSelectedFilterOptionsCount}`
                  }
                />
              )}
              {filterAndSortLabel}
              <IconFilter className="iconFilter" css={styles.iconFilter} />
            </button>
          </div>
        </div>
      </Media>
      <Media greaterThanOrEqual={Breakpoint.L} css={styles.largeScreenMenu}>
        <div css={[maxWidthContainer, grid]}>
          <div css={styles.filterBar}>
            <div css={styles.filterToolsWrapper}>
              <h2 aria-label={accessibleFilterLabel} css={styles.filterLabel}>
                {filterLabel}
              </h2>
              <div css={styles.resetButtonWrapper}>
                <TextButton
                  data-testid={elementIds.BUTTON_RESET_FILTERS}
                  onClick={onResetFilters}
                  aria-label={accessibleResetAllLabel}
                >
                  <span css={styles.resetButtonText}>{resetAllLabel}</span>
                </TextButton>
              </div>
              <p css={styles.totalProducts}>{totalProducts} products</p>
            </div>
            <div css={styles.sortByWrapper}>
              <SortByContainer />
            </div>
          </div>
        </div>
      </Media>
    </>
  );
};
